<template lang="pug">
  el-main(:class="$style.main")
    el-row.padding-top-15(:gutter="40")
      el-col(:span="24")
        el-table(
          :data="courses" 
          :class="$style.table" 
          border empty-text="No Courses"
          v-loading="loading"
        )
          el-table-column(label="Date" width="100" )
            template(v-slot="scope")
              | {{ new Date(scope.row.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) }}
          el-table-column(label="Instructor")
            template(v-slot="scope")
              | {{ scope.row.user.firstName }} {{ scope.row.user.lastName }}
          el-table-column(label="Course Code" width="200")
            template(v-slot="scope")
              | {{ scope.row.course.code.toUpperCase() }} 
          el-table-column(label="Title")
            template(v-slot="scope")
              div(style="display: flex; justify-content: space-between; align-items: center;")
                span {{ scope.row.course.title }}
                el-tooltip(
                  v-if="scope.row.course.description"
                  class="item"
                  effect="dark"
                  :content="'Description: ' + scope.row.course.description"
                  placement="top"
                )
                  i(class="el-icon-document" style="cursor: pointer;")
                i(
                  v-if="!scope.row.course.description"
                  class="el-icon-document"
                  style="opacity: 0.5;"
                )
          el-table-column(label="Email")
            template(v-slot="scope")
              a(:href="`mailto:${scope.row.email || user.email}`") {{ scope.row.email || user.email }}
          el-table-column(label="Status / Actions" width="250" align="center")
            template(v-slot="scope")
              template(v-if="!scope.row.status")
                el-button(
                  type="danger" 
                  size="mini" 
                  :disabled="processing"
                  @click="rejectApplication(scope.row)"
                )
                  i(:class="['el-icon--left', processing ? 'el-icon-loading' : 'el-icon-document-remove']") 
                  span Reject
                el-button(
                  type="success" 
                  size="mini" 
                  :disabled="processing"
                  @click="approveApplication(scope.row)"
                )
                  i(:class="['el-icon--left', processing ? 'el-icon-loading' : 'el-icon-document-checked']")
                  span Approve
              el-tag(
                v-else
                :type="scope.row.status === COURSE_APPROVED ? 'success' : 'danger'"
                style="font-weight: bold"
              ) {{ scope.row.status.toUpperCase() }} on {{ new Date(scope.row.resolvedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) }}
        el-pagination(
          :class="$style.paginationBar"
          :current-page="currentPage"
          layout="total, prev, pager, next"
          :page-size="pageSize"
          :total="itemTotal"
          @current-change="handlePageChange"
        )
</template>

<script>
  import { mapState } from 'vuex'
  import { ACTION_GET_APPLICATIONS, ACTION_GET_NOTIFICATION } from '@/masteradmin/store/actions'
  import { COURSE_APPROVED } from 'globalUtils/constants'
  import masterApiCall from '@/masteradmin/utils/api'

  export default {
    data() {
      return {
        COURSE_APPROVED,
        processing: false,
        currentPage: 1,
      }
    },

    computed: {
      ...mapState({
        courses: state => state.applications.applications,
        user: state => state.user.user,
        isAuthenticated: state => state.applications.isAuthenticated,
        itemTotal: state => state.applications.itemTotal,
        pageSize: state => state.applications.pageSize,
        loading: state => state.applications.loading,
        domain: state => state.courses.domain,
      }),
    },

    created() {
      document.title = 'HVU Master Admin: Applications List'
      this.getData()
    },

    methods: {
      getData() {
        this.$store.dispatch(ACTION_GET_APPLICATIONS, { page: this.currentPage })
        this.$store.dispatch(ACTION_GET_NOTIFICATION)
      },

      async resolveApplication(application, action = 'approve') {
        try {
          const { _id: id, user, course } = application
          const name = `${user.firstName} ${user.lastName}`
          const message = action === 'approve'
            ? `<ul><li> A new <b>${course.code}.${this.domain}</b> course will be created.<br><br>
              <li> ${name} will be the primary owner of the new course website.<br><br>
              <li> ${name} will receive an email message that their course application was approved.<br><br>
              </ul>
            `
            : `<ul>
              <li> ${name} will receive an email message that their course application was rejected.<br><br>
              <li> This application will be flagged as rejected. ${name} will be able to apply for a course again.<br><br>
              </ul>
            `
          this.$confirm(message, `${action.charAt(0).toUpperCase() + action.slice(1)} Application?`, {
            confirmButtonText: 'Yes',
            cancelButtonText: 'Cancel',
            dangerouslyUseHTMLString: true,
          }).then(async () => {
            this.processing = true
            await masterApiCall('post', `/masteradmin/applications/${action}/${id}`)
            this.$notify.success({
              title: 'Success',
              message: `Application was ${action}'d successfully`,
              duration: 10000,
              position: 'bottom-right',
            })
            this.getData()
          }).catch((error) => {
            if (error !== 'cancel') {
              throw error
            }
          }).finally(() => {
            this.processing = false
          })
        } catch (error) {
          const errorMessage = error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : `Failed to ${action} application`
          this.$notify.error({
            title: 'Action Error',
            message: errorMessage,
            duration: 10000,
            position: 'bottom-right',
          })
        }
      },

      approveApplication(application) {
        return this.resolveApplication(application, 'approve')
      },

      rejectApplication(application) {
        return this.resolveApplication(application, 'reject')
      },

      handlePageChange(page) {
        this.currentPage = page
        this.getData()
      },
    },
  }
</script>

<style module>
  .main {
    padding: 40px 5% * 5%;
  }
  
  .table {
    width: 100%;
    border-collapse: collapse;
    background-color: rgba(255, 255, 255, 0.55);
    color: var(--hvu-blue);
  }

  /* Style header cells within table */
  .table th {
    background-color: var(--button-blue) !important;
    color: white !important;
  }

  .paginationBar {
    margin-top: 24px;
    text-align: right;
    border-radius: 8px;
  }

  .paginationBar :global(.el-pagination__total) {
    color: white;
  }
</style>



// WEBPACK FOOTER //
// client/masteradmin/pages/ApplicationList/ApplicationList.vue